import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { InView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';

import { promoShape } from '@bibliocommons/bc-prop-types';
import Heading from '@bibliocommons/heading';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import cardShape from 'app/shapes/cardShape';
import CreativeCommons from 'app/components/cards/ContentCard/CreativeCommons';
import DebugCardButton from 'app/components/cards/ContentCard/DebugCardButton';
import EventDateStamp from 'app/components/cards/ContentCard/EventDateStamp';
import EventDateTime from 'app/components/shared/EventDateTime';

import './PromoCard.scss';

const isEvent = card => card.get('cardType') === 'event';

const getDescription = card => {
  if (!isEvent(card)) {
    return card.getIn(['description', 'text']);
  }

  const event = card.get('event', Immutable.Map());
  const start = event.get('startTime');
  const end = event.get('endTime');
  const manualDate = event.get('date');
  const shouldRenderManualDate = !(start || end) && Boolean(manualDate);

  return shouldRenderManualDate ? manualDate : <EventDateTime end={end} start={start} />;
};

const PromoCard = ({
  cmsUrl,
  hasDiscoveryLink,
  isDebugEnabled,
  promo,
  promoCard,
  trackImageClick,
  trackImpression,
  trackTitleClick,
  trackTypeClick
}) => {
  const intl = useIntl();
  const [hasImageLoadError, setHasImageLoadError] = useState(false);

  const cardType = promoCard.get('cardType', 'blog');
  const slug = promoCard.getIn(['contentType', 'slug']);
  const description = getDescription(promoCard);
  const image = promoCard.get('image');
  const locationName = promoCard.getIn(['location', 'name']);
  const locationUrl = promoCard.getIn(['location', 'url']);
  const title = promoCard.getIn(['title', 'text']);
  const url = promoCard.get('url');

  const isEventCard = isEvent(promoCard);
  const isListCard = cardType === 'list';
  const hasLocationUrl = Boolean(locationUrl);
  const hasLocationName = !hasLocationUrl && Boolean(locationName);

  const locationMessage = isEventCard ? (
    <>
      <span aria-hidden>{locationName}</span>
      <ScreenReaderMessage>
        <FormattedMessage id="sr_event_location" values={{ locationName }} />
      </ScreenReaderMessage>
    </>
  ) : (
    locationName
  );

  const imageMap = image.get('bwCardImage21', Immutable.Map());
  const imageAlt = imageMap.get('altText');
  const imageAttribution = imageMap.get('attribution');
  const imageUrl = imageMap.get('url');
  const isInformative = imageMap.get('isInformative');

  const discoveryUrl = `${cmsUrl}/explore?post-type=${slug}`;

  const handleVisibility = isVisible => {
    if (!isVisible) {
      return;
    }

    trackImpression();
  };

  const typeMessage = intl.formatMessage({
    defaultMessage: cardType,
    id: `card_type_${slug?.replace('-', '_')}`
  });

  return (
    <div className="cp-promo-card">
      <InView onChange={handleVisibility} threshold={1} triggerOnce>
        <div className="pc__image-wrapper">
          {isEventCard && (
            <div className="pc__date-stamp">
              <EventDateStamp card={Immutable.fromJS(promoCard)} size="default" />
            </div>
          )}
          <a
            aria-hidden={!isInformative}
            className="pc__image-wrapper-inner"
            href={url}
            onAuxClick={trackImageClick}
            onClick={trackImageClick}
          >
            {!hasImageLoadError && imageUrl && (
              <img
                alt={imageAlt}
                aria-hidden={!isInformative}
                className={`pc__image ${isListCard ? 'pc__image--list' : ''}`}
                onError={() => setHasImageLoadError(true)}
                loading="lazy"
                src={imageUrl}
              />
            )}
          </a>
          {imageAttribution && (
            <div aria-hidden={!isInformative}>
              <CreativeCommons attribution={imageAttribution} />
            </div>
          )}
        </div>
        <p className="pc__type">
          {hasDiscoveryLink ? (
            <a
              href={discoveryUrl}
              onAuxClick={() => trackTypeClick(typeMessage)}
              onClick={() => trackTypeClick(typeMessage)}
            >
              {typeMessage}
            </a>
          ) : (
            <span>{typeMessage}</span>
          )}
        </p>
        <Heading className="pc__heading" size="medium" tagName="h3">
          <a href={url} onAuxClick={() => trackTitleClick(title)} onClick={() => trackTitleClick(title)}>
            {title}
          </a>
        </Heading>
        <p className="pc__description">{description}</p>
        {hasLocationUrl && (
          <p className="pc__location">
            <a href={locationUrl}>{locationMessage}</a>
          </p>
        )}
        {hasLocationName && <p className="pc__location">{locationMessage}</p>}
        {isDebugEnabled && promo && (
          <div className="pc__debug-button">
            <DebugCardButton card={promoCard} promo={promo} />
          </div>
        )}
      </InView>
    </div>
  );
};

PromoCard.propTypes = {
  cmsUrl: PropTypes.string.isRequired,
  hasDiscoveryLink: PropTypes.bool.isRequired,
  isDebugEnabled: PropTypes.bool.isRequired,
  promo: promoShape.isRequired,
  promoCard: cardShape.isRequired,
  trackImageClick: PropTypes.func.isRequired,
  trackImpression: PropTypes.func.isRequired,
  trackTitleClick: PropTypes.func.isRequired,
  trackTypeClick: PropTypes.func.isRequired
};

export default PromoCard;
